import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Save, Plus, X } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ['Panneaux Solaires', 'Pompes', 'Électromécanique'];

const emptyForm = {
  name: '',
  description: '',
  price: '',
  category: CATEGORIES[0],
  image_url: '',
  brand: '',
  discount_percentage: 0,
  stock: 100,
  is_featured: false,
  specs: [],
};

export const AdminProductForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { authHeaders } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    axios
      .get(`${BACKEND}/api/products/${id}`)
      .then(({ data }) => {
        setForm({
          name: data.name || '',
          description: data.description || '',
          price: data.price ?? '',
          category: data.category || CATEGORIES[0],
          image_url: data.image_url || '',
          brand: data.brand || '',
          discount_percentage: data.discount_percentage ?? 0,
          stock: data.stock ?? 0,
          is_featured: Boolean(data.is_featured),
          specs: data.specs ? Object.entries(data.specs).map(([k, v]) => ({ k, v })) : [],
        });
      })
      .catch(() => toast.error('Produit introuvable'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const update = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const addSpec = () => setForm((f) => ({ ...f, specs: [...f.specs, { k: '', v: '' }] }));
  const removeSpec = (i) => setForm((f) => ({ ...f, specs: f.specs.filter((_, idx) => idx !== i) }));
  const updateSpec = (i, key, val) =>
    setForm((f) => ({ ...f, specs: f.specs.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const specsObj = {};
    form.specs.forEach((s) => {
      if (s.k.trim()) specsObj[s.k.trim()] = s.v;
    });

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      price: parseFloat(form.price),
      category: form.category,
      image_url: form.image_url.trim(),
      brand: form.brand.trim() || null,
      discount_percentage: parseFloat(form.discount_percentage) || 0,
      stock: parseInt(form.stock, 10) || 0,
      is_featured: form.is_featured,
      specs: Object.keys(specsObj).length ? specsObj : null,
    };

    try {
      if (isEdit) {
        await axios.put(`${BACKEND}/api/products/${id}`, payload, { headers: authHeaders() });
        toast.success('Produit mis à jour');
      } else {
        await axios.post(`${BACKEND}/api/products`, payload, { headers: authHeaders() });
        toast.success('Produit créé');
      }
      navigate('/admin');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Chargement…</div>;
  }

  const priceNum = parseFloat(form.price) || 0;
  const discountNum = parseFloat(form.discount_percentage) || 0;
  const finalPrice = priceNum * (1 - discountNum / 100);

  return (
    <div className="min-h-screen bg-muted/30 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/admin')}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-4"
          data-testid="admin-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>

        <h1 className="font-heading font-bold text-4xl text-secondary mb-8" data-testid="admin-form-title">
          {isEdit ? 'Modifier le produit' : 'Nouveau produit'}
        </h1>

        <form onSubmit={handleSubmit} className="bg-white border border-border rounded-xl p-6 md:p-8 space-y-6">
          {/* Basic info */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-secondary mb-2">Nom du produit *</label>
              <input
                required
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-testid="form-name-input"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-secondary mb-2">Description *</label>
              <textarea
                required
                rows={4}
                value={form.description}
                onChange={(e) => update('description', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-testid="form-description-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary mb-2">Catégorie *</label>
              <select
                value={form.category}
                onChange={(e) => update('category', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-testid="form-category-select"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary mb-2">Marque</label>
              <input
                value={form.brand}
                onChange={(e) => update('brand', e.target.value)}
                placeholder="Ex: SolarTech"
                className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-testid="form-brand-input"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-secondary mb-2">URL de l'image *</label>
              <input
                required
                type="url"
                value={form.image_url}
                onChange={(e) => update('image_url', e.target.value)}
                placeholder="https://..."
                className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-testid="form-image-url-input"
              />
              {form.image_url && (
                <img src={form.image_url} alt="preview" className="mt-3 w-32 h-32 object-cover rounded-lg border border-border" />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary mb-2">Prix (DH) *</label>
              <input
                required
                type="number"
                step="0.01"
                min="0"
                value={form.price}
                onChange={(e) => update('price', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-testid="form-price-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary mb-2">Réduction (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={form.discount_percentage}
                onChange={(e) => update('discount_percentage', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-testid="form-discount-input"
              />
              {discountNum > 0 && priceNum > 0 && (
                <p className="text-xs text-accent font-medium mt-1">
                  Prix final : {finalPrice.toFixed(2)} DH
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary mb-2">Stock</label>
              <input
                type="number"
                min="0"
                value={form.stock}
                onChange={(e) => update('stock', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-testid="form-stock-input"
              />
            </div>

            <div className="flex items-center gap-3 pt-8">
              <input
                id="is_featured"
                type="checkbox"
                checked={form.is_featured}
                onChange={(e) => update('is_featured', e.target.checked)}
                className="w-5 h-5 accent-primary"
                data-testid="form-featured-checkbox"
              />
              <label htmlFor="is_featured" className="text-sm font-medium text-secondary">
                Mettre en vedette
              </label>
            </div>
          </div>

          {/* Specs */}
          <div className="pt-4 border-t border-border">
            <div className="flex items-center justify-between mb-4">
              <label className="text-sm font-medium text-secondary">Spécifications techniques</label>
              <button
                type="button"
                onClick={addSpec}
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                data-testid="form-add-spec-btn"
              >
                <Plus className="w-4 h-4" /> Ajouter
              </button>
            </div>
            <div className="space-y-3">
              {form.specs.map((s, i) => (
                <div key={i} className="flex gap-3">
                  <input
                    placeholder="Nom (ex: Puissance)"
                    value={s.k}
                    onChange={(e) => updateSpec(i, 'k', e.target.value)}
                    className="flex-1 px-3 py-2 border border-border rounded-lg text-sm"
                  />
                  <input
                    placeholder="Valeur (ex: 400W)"
                    value={s.v}
                    onChange={(e) => updateSpec(i, 'v', e.target.value)}
                    className="flex-1 px-3 py-2 border border-border rounded-lg text-sm"
                  />
                  <button type="button" onClick={() => removeSpec(i)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {form.specs.length === 0 && (
                <p className="text-sm text-muted-foreground italic">Aucune spécification. Cliquez sur "Ajouter".</p>
              )}
            </div>
          </div>

          <div className="flex gap-3 pt-6 border-t border-border">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-3 rounded-lg font-medium disabled:opacity-50"
              data-testid="form-submit-btn"
            >
              <Save className="w-4 h-4" />
              {submitting ? 'Enregistrement…' : isEdit ? 'Enregistrer' : 'Créer le produit'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin')}
              className="px-6 py-3 border border-border rounded-lg font-medium text-secondary hover:bg-muted"
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
